<template>
  <b-modal
    id="create-child-modal"
    ref="createChildModal"
    size="xl"
    header-bg-variant="white"
    ok-variant="secondary"
    no-footer
    @show="onShowModal"
    @hide="onHideModal"
    @close="onHideModal"
  >
    <!-- Title -->
    <template #title>
      <span v-if="!selectedChildData"><i class="fa fa-plus"></i> {{ $t("Create child") }} </span>
      <span v-else
        ><i class="fa fa-edit"></i> {{ $t("You're editing:") }}
        <span v-if="selectedChildData"
          >: {{ selectedChildData.lastName }} {{ selectedChildData.firstName }}</span
        ></span
      >
    </template>

    <ParentChildEditForm
      v-if="variant !== 'register' || districts"
      variant="modal"
      :classrooms="classroomsForForm"
      :allergens="allergens"
      :price-lists-groups="priceListsGroups"
      :is-register="variant === 'register'"
      :districts="districts"
      :selected-child-data="selectedChildData"
      :submit="onSubmit"
      :is-visible="isVisible"
      :default-institution-id="defaultInstitutionId"
      :default-institution-name="defaultInstitutionName"
      @institution-change="queryClassrooms"
    />
  </b-modal>
</template>

<script>
import ParentChildEditForm from "@/modules/parents/components/ParentChildEditForm.vue";
import { mapGetters, mapMutations } from "vuex";
import {
  allowCreatingChildren,
  hideInstitutionsListsFromParents,
} from "@/modules/settings/queries/tenantSettingsSelectors";

export default {
  name: "CreateChildModal",

  components: {
    ParentChildEditForm,
  },

  props: {
    isVisible: {
      type: Boolean,
      default: false,
      required: true,
    },
    // eslint-disable-next-line vue/require-default-prop
    parent: null,
    variant: {
      type: String,
      default: "default",
    },
    // eslint-disable-next-line vue/require-default-prop
    selectedChildData: null,
    defaultInstitutionId: {
      type: Number,
      default: null,
    },
    defaultInstitutionName: {
      type: String,
      default: null,
    },
    // slug behind defaultInstitutionId when it came from a registration code (route or manually
    // entered) - sent to childOptions so it scopes institutions/classrooms to it
    institutionSlug: {
      type: String,
      default: null,
    },
  },

  emits: ["hide", "childDataEdited", "childDataCreated", "childCreated"],

  setup() {
    return {
      allowCreatingChildren,
      hideInstitutionsListsFromParentsFromSettings: hideInstitutionsListsFromParents,
    };
  },

  data() {
    return {
      districts: null,
      institutions: null,
      allClassrooms: [],
      classrooms: [],
      // register variant only - the institution to filter allClassrooms by; null until one is
      // picked (or preselected from a registration code), so classroomsForForm stays empty until then
      selectedInstitutionId: null,
      allergens: [],
      priceListsGroups: [],
      settingsPresencesMarkingConfig: null,
      hideInstitutionsListsFromParents: false,
    };
  },
  computed: {
    ...mapGetters({
      isAuthenticated: "auth/isAuthenticated",
      isParent: "auth/isParent",
      isAdmin: "auth/isAdmin",
    }),

    canCreateChild() {
      return this.variant === "register" || !this.isParent || this.allowCreatingChildren;
    },

    // register variant derives the list reactively from allClassrooms + selectedInstitutionId
    // instead of assigning it imperatively, so there is no window where it shows every
    // institution's classrooms before the selected one is known
    classroomsForForm() {
      if (this.variant !== "register") {
        return this.classrooms;
      }

      return this.allClassrooms.filter(
        (classroom) =>
          classroom.value === null || classroom.institutionId === this.selectedInstitutionId,
      );
    },
  },
  watch: {
    isVisible(newValue) {
      if (!newValue) {
        return;
      }

      if (!this.canCreateChild) {
        this.showNotification({
          type: "warning",
          text: this.$t("Adding new children has been disabled for your account."),
        });

        this.$emit("hide");

        return;
      }

      this.$refs.createChildModal.show();
    },
  },

  async beforeMount() {
    this.hideInstitutionsListsFromParents = this.hideInstitutionsListsFromParentsFromSettings;

    // anonymous registration has no auth token for these endpoints
    if (this.variant === "register") {
      return;
    }

    const result = await this.$store.dispatch("allergens/getMany", { perPage: 9999 });

    // price lists groups are admin-only: the picker in ParentChildEditForm only renders for
    // admins (canModifyPriceListsGroup), and non-admins get a 403 fetching this endpoint
    if (this.isAdmin) {
      const priceListsGroups = await this.$store.dispatch("priceLists/getManyPriceListsGroups", {
        perPage: 9999,
        withoutPriceListsGroupForGroupOrders: true,
      });

      this.setPriceListsGroups(priceListsGroups.items);
    }

    this.setAllergens(result.items);
  },

  async mounted() {
    // register options overwrite the shared institutions/districts options
    if (this.variant !== "register") {
      return;
    }

    // just to be sure that it will be called before anything else
    // can be called twice because result is stored in vuex state
    await this.$store.dispatch("auth/getChildRegisterOptions", this.institutionSlug);
  },

  methods: {
    ...mapMutations({
      showNotification: "notifications/showNotification",
    }),

    onHideModal() {
      this.$emit("hide");
    },

    async onShowModal() {
      if (this.variant === "register") {
        try {
          const result = await this.$store.dispatch(
            "auth/getChildRegisterOptions",
            this.institutionSlug,
          );

          this.allergens = result.allergensOptions;
          this.allClassrooms = result.classroomsOptions;
          this.districts = result.districtsOptions;
        } catch {
          // keeps districts from staying null forever, which would leave the modal blank
          this.districts = [];
        }
      }
    },

    async onSubmit(childData) {
      if (!this.canCreateChild) {
        this.$refs.createChildModal.hide();
        return;
      }

      if (this.variant === "register") {
        if (this.selectedChildData) {
          this.$emit("childDataEdited", childData);
        } else {
          this.$emit("childDataCreated", childData);
        }

        this.$refs.createChildModal.hide();

        return;
      }

      childData.parentId = this.parent?.id || this.parent;

      const result = await this.$store.dispatch("children/create", childData);

      if (result) {
        this.showNotification({
          type: "success",
          text: this.$t("Child '{child}' has been created.", {
            child: result.fullName.name,
          }),
        });

        this.$emit("childCreated", result);

        this.$refs.createChildModal.hide();
      }
    },

    setClassrooms(classrooms) {
      this.classrooms = classrooms;
    },

    async queryClassrooms(institutionId) {
      if (this.variant === "register") {
        this.selectedInstitutionId = institutionId;

        return;
      }

      const classrooms = await this.$store.dispatch("classrooms/getMany", {
        institutionId: institutionId,
        page: 1,
        perPage: 9999,
      });

      this.setClassrooms(classrooms.items);
    },

    setAllergens(allergens) {
      this.allergens = allergens;
    },

    setPriceListsGroups(priceListsGroups) {
      this.priceListsGroups = priceListsGroups;
    },
  },
};
</script>
